import React, { useEffect, useState, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../Utils/Common";
import {
  SYSTEM_NOTIFICATIONS_ALL_API,
  SYSTEM_NOTIFICATIONS_CREATE_API,
  getSystemNotificationUpdateUrl,
  SUCCESS_STATUS,
} from "../../api/constants";
import { semcastAxios } from "../../Utils/Common";
import NotificationBanner from "./SharedComponents/SystemNotificationBanner";
import "./SystemNotificationCreator.css";
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';

const SystemNotificationCreator = () => {
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [expiresAt, setExpiresAt] = useState("2023-04-15T12:00");
  const [passcode, setPasscode] = useState("");
  const [status, setStatus] = useState("active");
  const [sections, setSections] = useState([{ text: "" }]);
  const [notifications, setNotifications] = useState([]);
  const exportRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await semcastAxios.get(SYSTEM_NOTIFICATIONS_ALL_API, {
        withCredentials: true,
        headers: { "Cache-Control": "no-cache" },
      });
      if (res?.data?.status === SUCCESS_STATUS) {
        const data = res.data.data.map((n) => {
          const content =
            typeof n.contentJson === "string"
              ? JSON.parse(n.contentJson)
              : n.contentJson;
          return {
            ...n,
            content,
            formattedExpires: n.expiresAt
              ? new Date(n.expiresAt).toLocaleString()
              : "",
            formattedCreated: n.createdAt
              ? new Date(n.createdAt).toLocaleString()
              : "",
            formattedUpdated: n.updatedAt
              ? new Date(n.updatedAt).toLocaleString()
              : "",
          };
        });
        setNotifications(data);
      } else {
        showErrorToastMessage("Failed to load notifications");
      }
    } catch (e) {
      console.error(e);
      showErrorToastMessage("Server error while fetching notifications");
    }
  };

  const addSection = () => setSections((prev) => [...prev, { text: "" }]);
  const removeSection = (index) =>
    setSections((prev) => prev.filter((_, i) => i !== index));
  const updateSectionText = (index, value) =>
    setSections((prev) =>
      prev.map((sec, i) => (i === index ? { ...sec, text: value } : sec))
    );

  const handleEdit = (notif) => {
    setEditingId(notif.id);
    setTitle(notif.displayTitle || "");
    setExpiresAt(
      notif.expiresAt
        ? new Date(notif.expiresAt).toISOString().slice(0, 16)
        : ""
    );
    setSections(notif.content?.sections || [{ text: "" }]);
    setStatus(notif.status || "inactive");
    setPasscode("");
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setExpiresAt("2025-04-15T12:00");
    setPasscode("");
    setStatus("active");
    setSections([{ text: "" }]);
  };

  const validateForm = () => {
    if (!title.trim() && !sections.some((s) => s.text.trim())) {
      showErrorToastMessage("At least a title or a section is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const now = new Date();
    const expiration = expiresAt ? new Date(expiresAt) : null;

    if (!editingId) {
      const hasActive = notifications.some(
        (n) =>
          n.status === "active" &&
          (!n.expiresAt || new Date(n.expiresAt).getTime() > Date.now())
      );
      if (hasActive) {
        const proceed = window.confirm(
          "⚠️ An active notification already exists.\n\nAre you sure you want to create another one?\nIt's recommended to check existing active notifications before proceeding."
        );
        if (!proceed) return;
      }
    }

    if (status === "active" && expiration && expiration <= now) {
      showErrorToastMessage(
        "Cannot set notification to active — expiration must be in the future."
      );
      return;
    }

    const payload = {
      id: editingId,
      passcode,
      displayTitle: title,
      status,
      expiresAt: expiration ? expiration.toISOString() : null,
      contentJson: JSON.stringify({
        title,
        sections: sections.filter((sec) => sec.text.trim()),
      }),
    };

    try {
      const url = editingId
        ? getSystemNotificationUpdateUrl(editingId)
        : SYSTEM_NOTIFICATIONS_CREATE_API;
      const method = editingId ? "put" : "post";

      const res = await semcastAxios[method](url, payload, {
        withCredentials: true,
      });

      if (res?.data?.status === SUCCESS_STATUS) {
        showSuccessToastMessage(
          editingId ? "Updated successfully" : "Created successfully"
        );
        resetForm();
        fetchNotifications();
      } else {
        const errorMessage =
          res?.data?.message || "API error occurred. Please try again.";
        showErrorToastMessage(errorMessage);
      }
    } catch (err) {
      console.error(err);
      const backendMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Request failed. Please try again.";
      showErrorToastMessage(backendMessage);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const handleExportClick = () => {
    if (exportRef.current) {
      exportRef.current.save();
    }
  };

  return (
    <div className="system-notification-container">
      <div className="system-notification-form">
        <h3 className="field-label-large">{editingId ? "Update System Notification" : "Create New System Notification"}</h3>

        <div className="form-group">
          <label className="form-label">Display Title</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Expiration</label>
          <input
            type="datetime-local"
            className="form-control"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-control"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <h5 className="field-label-large">Notification Sections</h5>
        <p className="section-helper-text">
          💡 You can include links using HTML. For example: {" "}
          <code>&lt;a href="https://example.com"&gt;Click here&lt;/a&gt;</code>
        </p>

        {sections.map((sec, index) => (
          <div key={index} className="section-item">
            <label className="form-label">Section {index + 1} Text</label>
            <textarea
              rows={2}
              className="form-control"
              value={sec.text}
              onChange={(e) => updateSectionText(index, e.target.value)}
            />
            {sections.length > 1 && (
              <Button
                className="button-white"
                style={{ marginTop: "5px" }}
                onClick={() => removeSection(index)}
              >
                Remove
              </Button>
            )}
          </div>
        ))}

        <Button className="button-submit" onClick={addSection}>
          Add Another Section
        </Button>

        <div className="form-group mt-3">
          <label className="form-label">Passcode</label>
          <input
            type="password"
            className="form-control"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder={`Enter "development" or "production"`}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="form-group preview-section">
          <h4 className="field-label-large">Notification Preview</h4>
          <NotificationBanner
            notif={{
              content_json: JSON.stringify({
                title,
                sections: sections.filter((sec) => sec.text.trim()),
              }),
            }}
          />
        </div>

        <div className="button-row mt-4">
          {editingId && (
            <Button
              className="button-white"
              onClick={resetForm}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
          )}
          <Button className="button-submit" onClick={handleSubmit}>
            {editingId ? "Update Notification" : "Create Notification"}
          </Button>
        </div>
      </div>

      <div className="system-notification-table">
        <div className="table-header-row">
          <h3 className="field-label-large">Existing System Notifications</h3>
            <a
            href="javascript:void(0)"
            className="btn btn-primary float-right"
            title="CSV"
            onClick={handleExportClick}
          >
            <i className="fa fa-arrow-down" style={{ marginRight: "5px" }}></i>
            <span className="button-submit">as CSV</span>
          </a>
        </div>

        <ExcelExport ref={exportRef} data={notifications} fileName="System_Notifications.xlsx">
          <ExcelExportColumn field="id" title="ID" width={50} />
          <ExcelExportColumn field="displayTitle" title="Display Title" width={200} />
          <ExcelExportColumn field="status" title="Status" width={100} />
          <ExcelExportColumn field="formattedExpires" title="Expires" width={150} />
          <ExcelExportColumn field="formattedCreated" title="Created" width={150} />
          <ExcelExportColumn field="formattedUpdated" title="Updated" width={150} />
        </ExcelExport>
        <Grid data={notifications} style={{ width: "100%" }}>
        <Column field="id" title="ID" width="60px" />
        <Column field="displayTitle" title="Display Title" />
        <Column field="status" title="Status" width="120px" />
        <Column field="formattedExpires" title="Expires" width="150px" />
        <Column field="formattedCreated" title="Created" width="150px" />
        <Column field="formattedUpdated" title="Updated" width="150px" />
        <Column
          title="Action"
    width="100px"
    cell={(props) => (
      <td>
<Button
  className="k-button k-button-sm k-button-rectangle k-button-solid k-button-solid-base k-rounded-sm button-standard button-submit fit-action-column"
  onClick={() => handleEdit(props.dataItem)}
>
  Edit
</Button>


      </td>
          )}
        />    
        </Grid>

          
      </div>
    </div>
  );
};

export default SystemNotificationCreator;
